import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Copy, Trash2, FileText, Clock, CheckCircle2, Calculator } from "lucide-react";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { fmt, STATUS_LABEL, STATUS_STYLE, TYPE_LABEL } from "./shared";

const inputCls = "h-9 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";

export function CalculationsList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: (type: string) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [menu, setMenu] = useState(false);

  const query = useQuery({
    queryKey: ["calculations", q, status, type, from, to],
    queryFn: () => api.listCalculations({ q, status, type, from, to }),
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.calculationAction(id, "duplicate"),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["calculations"] });
      onOpen(c.id);
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteCalculation(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["calculations"] }),
  });

  const stats = query.data?.stats;
  const cards = [
    { label: "Nacrti", value: stats?.draft ?? 0, icon: FileText, cls: "text-slate-600 bg-slate-100" },
    { label: "Na odobravanju", value: stats?.pendingApproval ?? 0, icon: Clock, cls: "text-amber-600 bg-amber-50" },
    { label: "Proknjiženo ovog meseca", value: stats?.postedThisMonth ?? 0, icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Kalkulacije</h2>
          <p className="text-sm text-slate-500">Obračun nabavne vrednosti, zavisnih troškova i prodajnih cena</p>
        </div>
        <div className="relative">
          <Button onClick={() => setMenu((v) => !v)}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> Nova kalkulacija
          </Button>
          {menu && (
            <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-border bg-surface p-1 shadow-lg">
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => {
                    setMenu(false);
                    onNew(k);
                  }}
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100"
                >
                  {v}
                </button>
              ))}
              {["Kalkulacija usluga", "Maloprodajna", "Međumagacinska"].map((v) => (
                <div key={v} className="flex items-center justify-between px-3 py-2 text-sm text-slate-400">
                  {v} <span className="rounded bg-slate-100 px-1.5 text-[10px]">Uskoro</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
            <div className={`rounded-lg p-2 ${c.cls}`}>
              <c.icon className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <div className="text-2xl font-semibold text-slate-900">{c.value}</div>
              <div className="text-xs text-slate-500">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} w-64`} placeholder="Pretraga: broj, faktura, dobavljač" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Svi statusi</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Svi tipovi</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Od datuma" />
        <input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} aria-label="Do datuma" />
      </div>

      {query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !query.data?.items.length ? (
        <EmptyState icon={Calculator} title="Nema kalkulacija" description="Napravite prvu kalkulaciju - možete je napraviti direktno iz fakture dobavljača iz SEF-a." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Broj</th>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Tip</th>
                <th className="px-3 py-2">Dobavljač</th>
                <th className="px-3 py-2">Faktura</th>
                <th className="px-3 py-2 text-right">Nabavna vr.</th>
                <th className="px-3 py-2 text-right">Troškovi</th>
                <th className="px-3 py-2 text-right">Ukupno</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((r) => (
                <tr key={r.id} className="cursor-pointer border-t border-border hover:bg-slate-50" onClick={() => onOpen(r.id)}>
                  <td className="px-3 py-2 font-medium text-brand">{r.number}</td>
                  <td className="px-3 py-2">{new Date(r.documentDate).toLocaleDateString("sr-RS")}</td>
                  <td className="px-3 py-2">{TYPE_LABEL[r.type] ?? r.type}</td>
                  <td className="px-3 py-2">{r.supplierName ?? "—"}</td>
                  <td className="px-3 py-2">{r.sourceDocNumber ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.totalPurchase)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.totalCosts)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.totalGross)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] ?? ""}`}>{STATUS_LABEL[r.status] ?? r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button title="Kopiraj" className="rounded p-1 text-slate-400 hover:bg-slate-100" onClick={() => dup.mutate(r.id)}>
                      <Copy className="h-4 w-4" aria-hidden />
                    </button>
                    {r.status !== "POSTED" && r.status !== "CANCELLED" && (
                      <button
                        title="Obriši"
                        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        onClick={() => window.confirm("Obrisati kalkulaciju?") && del.mutate(r.id)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
